import hoistNonReactStatics from 'hoist-non-react-statics';
import { ComponentType, createContext, ReactNode, useContext, useMemo, VFC } from 'react';
import getDisplayName from 'react-display-name';

export class FeatureFlags {
  static enabledFlags: string[] = window.ciq?.enabledFeatureFlags || [];

  static isFeatureEnabled(flagName: string): boolean {
    const ffEnabled = FeatureFlags.enabledFlags.includes(flagName);
    if (window.DD_RUM) {
      window.DD_RUM?.addFeatureFlagEvaluation(flagName, ffEnabled);
    }
    return ffEnabled;
  }
}

export type FeatureFlagsContextType = {
  enabledFeatureFlags: string[];
  isFeatureEnabled: (featureName: string) => boolean;
};

const DEFAULT_CONTEXT: FeatureFlagsContextType = {
  enabledFeatureFlags: FeatureFlags.enabledFlags,
  isFeatureEnabled: FeatureFlags.isFeatureEnabled,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>(DEFAULT_CONTEXT);
FeatureFlagsContext.displayName = 'FeatureFlagsContext';

export const FeatureFlagsProvider = ({
  enabledFeatureFlags = [],
  children,
}: {
  enabledFeatureFlags?: string[];
  children: ReactNode;
}) => {
  const inherited = useContext(FeatureFlagsContext);
  if (inherited !== DEFAULT_CONTEXT) {
    console.error('FeatureFlagsProvider should not be nested.');
  }

  if (enabledFeatureFlags.length > 0) {
    FeatureFlags.enabledFlags = enabledFeatureFlags;
  }

  const value = useMemo<FeatureFlagsContextType>(
    () => ({
      enabledFeatureFlags: enabledFeatureFlags.length > 0 ? enabledFeatureFlags : FeatureFlags.enabledFlags,
      isFeatureEnabled: FeatureFlags.isFeatureEnabled,
    }),
    [enabledFeatureFlags],
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
export const useIsFeatureEnabled = (featureName: string) =>
  useContext(FeatureFlagsContext).isFeatureEnabled(featureName);

export function withFeatureFlags<Props extends FeatureFlagsContextType>(WrappedComponent: ComponentType<Props>) {
  const displayName = `WithFeatureFlags(${getDisplayName(WrappedComponent)})`;

  const WithFeatureFlags: VFC<Omit<Props, keyof FeatureFlagsContextType>> = (props) => {
    return (
      <FeatureFlagsContext.Consumer>
        {(values) => <WrappedComponent {...(props as Props)} {...values} />}
      </FeatureFlagsContext.Consumer>
    );
  };

  WithFeatureFlags.displayName = displayName;

  hoistNonReactStatics(WithFeatureFlags, WrappedComponent);

  return WithFeatureFlags;
}
